import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  FaBold,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaFileAlt,
  FaGlobe,
  FaImage,
  FaItalic,
  FaLink,
  FaListUl,
  FaMapMarkerAlt,
  FaMinusCircle,
  FaPhone,
  FaPlus,
  FaSmile,
  FaTrash,
} from 'react-icons/fa';
import {
  createWaTemplate,
  getToken as getStoredToken,
  uploadTemplateMedia,
} from '../../../../lib/api';
import '../../styles/template-builder.css';

Modal.setAppElement?.('#root');

const HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'];
const MAX_BUTTONS = 10;
const MAX_WEBSITE_BUTTONS = 2;

const createDraftId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const defaultButtonDraft = (kind) => ({
  id: createDraftId(),
  kind,
  isSaved: false,
  fields: {},
});

const mapDraftToButton = (kind, fields, id) => {
  switch (kind) {
    case 'URL':
      return {
        id,
        type: 'URL',
        text: fields.label,
        url: fields.url,
      };
    case 'OFFER_CODE':
      return {
        id,
        type: 'OFFER_CODE',
        text: fields.offerCode || '',
      };
    case 'PHONE_NUMBER': {
      const phoneButton = {
        id,
        type: 'PHONE_NUMBER',
        text: fields.label,
      };
      if (fields.phoneNumber) {
        phoneButton.phone_number = `+91${fields.phoneNumber}`;
      }
      return phoneButton;
    }
    default:
      return {
        id,
        type: 'QUICK_REPLY',
        text: fields.text,
      };
  }
};

const labelForButtonKind = (kind) => {
  switch (kind) {
    case 'URL':
      return 'View website';
    case 'OFFER_CODE':
      return 'COPY_CODE';
    case 'PHONE_NUMBER':
      return 'Phone number';
    default:
      return 'Quick reply';
  }
};

const validateDraft = (draft) => {
  const cleaned = Object.entries(draft.fields || {}).reduce((acc, [key, value]) => {
    acc[key] = typeof value === 'string' ? value.trim() : value;
    return acc;
  }, {});

  switch (draft.kind) {
    case 'URL':
      if (!cleaned.url || !/^https?:\/\//.test(cleaned.url)) {
        return { ok: false, message: 'Enter a valid URL (must start with http or https).' };
      }
      if (!cleaned.label) {
        return { ok: false, message: 'Provide button text for the website button.' };
      }
      return { ok: true, cleaned };
    case 'OFFER_CODE':
      if (!cleaned.offerCode) return { ok: false, message: 'Offer code cannot be empty.' };
      return { ok: true, cleaned };
    case 'PHONE_NUMBER':
      if (!cleaned.phoneNumber || cleaned.phoneNumber.length !== 10) {
        return { ok: false, message: 'Phone number must be exactly 10 digits.' };
      }
      if (!cleaned.label) return { ok: false, message: 'Provide button text for the phone button.' };
      return { ok: true, cleaned };
    default:
      if (!cleaned.text) return { ok: false, message: 'Quick reply text cannot be empty.' };
      return { ok: true, cleaned };
  }
};

const TemplateBuilder = ({ categoryLabel, optionLabel = 'Custom', onBack }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [templateName, setTemplateName] = useState('your_template_name');
  const [language, setLanguage] = useState('en_US');
  const [headerType, setHeaderType] = useState('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerVariableEnabled, setHeaderVariableEnabled] = useState(false);
  const [headerVariableValue, setHeaderVariableValue] = useState('');
  const [headerMediaPreview, setHeaderMediaPreview] = useState(null);
  const [headerMediaHandle, setHeaderMediaHandle] = useState(null);
  const [bodyText, setBodyText] = useState('');
  const [variableFields, setVariableFields] = useState([]);
  const [nextVariableIndex, setNextVariableIndex] = useState(1);
  const [footerText, setFooterText] = useState('');
  const [buttonDrafts, setButtonDrafts] = useState([]);
  const [buttonError, setButtonError] = useState('');
  const [uploadState, setUploadState] = useState({ open: false, loading: false, message: '' });
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [successModal, setSuccessModal] = useState(false);

  const updateHeaderFileAccept = useCallback(
    (type) => {
      if (!fileInputRef.current) return;
      let accept = 'image/*';
      if (type === 'VIDEO') {
        accept = 'video/*';
      } else if (type === 'DOCUMENT') {
        accept =
          '.pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }
      fileInputRef.current.accept = accept;
    },
    [fileInputRef]
  );

  const upperCategory = useMemo(() => (categoryLabel ? categoryLabel.toUpperCase() : 'MARKETING'), [categoryLabel]);
  const optionDisplay = optionLabel || 'Custom';

  const cleanText = useCallback((text) => {
    if (!text) return '';
    return text
      .replace(/[ \t]+([,.!?;:])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .trim();
  }, []);

  const removeVariableTokens = useCallback(
    (text, index) => {
      if (!text) return '';
      const tokenPattern = new RegExp(`\\s*\\{\\{${index}\\}\\}\\s*`, 'g');
      const fallbackPattern = new RegExp(`\\{\\{${index}\\}\\}`, 'g');
      let next = text.replace(tokenPattern, ' ');
      next = next.replace(fallbackPattern, ' ');
      return cleanText(next);
    },
    [cleanText]
  );

  const savedButtons = useMemo(
    () =>
      buttonDrafts
        .filter((draft) => draft.isSaved)
        .map((draft) => mapDraftToButton(draft.kind, draft.fields, draft.id)),
    [buttonDrafts]
  );

  const buttonsForPreview = useMemo(
    () => savedButtons.map(({ id, ...rest }) => rest),
    [savedButtons]
  );

  useEffect(() => {
    Modal.setAppElement?.('#root');
  }, []);

  // Marketing templates: add one default Quick Reply button (editable text), fixed for this category only
  useEffect(() => {
    if (upperCategory === 'MARKETING' && buttonDrafts.length === 0) {
      setButtonDrafts([{ ...defaultButtonDraft('QUICK_REPLY'), fields: { text: 'Reply' } }]);
    }
  }, [upperCategory, buttonDrafts.length]);

  const resetHeader = useCallback(() => {
    setHeaderText('');
    setHeaderVariableEnabled(false);
    setHeaderVariableValue('');
    setHeaderMediaPreview(null);
    setHeaderMediaHandle(null);
  }, []);

  const handleHeaderTypeChange = (event) => {
    const value = event.target.value;
    setHeaderType(value);
    resetHeader();
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(value)) {
      updateHeaderFileAccept(value);
      fileInputRef.current?.click();
    }
  };

  const handleHeaderVariableToggle = () => {
    if (headerVariableEnabled) {
      setHeaderVariableEnabled(false);
      setHeaderVariableValue('');
      setHeaderText((prev) => removeVariableTokens(prev, 1));
    } else {
      setHeaderVariableEnabled(true);
      if (!/{{1}}/.test(headerText)) {
        setHeaderText((prev) => `${prev.trim()} {{1}}`.trim());
      }
    }
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValid = validateFileForHeader(file, headerType);
    if (!isValid) {
      setErrorModal({ open: true, message: 'Selected file type is not supported for this header.' });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setHeaderMediaPreview({ url: previewUrl, name: file.name, type: file.type });

    try {
      setUploadState({ open: true, loading: true, message: 'Uploading…' });
      const response = await uploadTemplateMedia(file);
      setHeaderMediaHandle(response);
      setUploadState({ open: true, loading: false, message: 'Upload successful' });
      setTimeout(() => setUploadState({ open: false, loading: false, message: '' }), 1200);
    } catch (err) {
      console.error('uploadTemplateMedia', err);
      setUploadState({ open: true, loading: false, message: 'Upload failed. Please try again.' });
    }
  };

  const handleBodyVariableAdd = () => {
    setBodyText((prev) => `${prev}${prev.endsWith(' ') || prev.length === 0 ? '' : ' '}{{${nextVariableIndex}}}`);
    setNextVariableIndex((prev) => prev + 1);
  };

  const extractVariables = useCallback((text) => {
    const matches = [...text.matchAll(/{{(\d+)}}/g)].map((match) => Number(match[1]));
    const unique = Array.from(new Set(matches)).sort((a, b) => a - b);
    return unique;
  }, []);

  useEffect(() => {
    const indexes = extractVariables(bodyText);
    setVariableFields((prev) => {
      const existingMap = new Map(prev.map((item) => [item.index, item.value]));
      return indexes.map((index) => ({ index, value: existingMap.get(index) || '' }));
    });
    const max = indexes.length ? Math.max(...indexes) : 0;
    setNextVariableIndex(max + 1);
  }, [bodyText, extractVariables]);

  const handleButtonDraftAdd = (kind) => {
    if (savedButtons.length >= MAX_BUTTONS) {
      setButtonError('You can add up to 10 buttons.');
      return;
    }
    if (kind === 'URL') {
      const existing = savedButtons.filter((button) => button.type === 'URL').length;
      if (existing >= MAX_WEBSITE_BUTTONS) {
        setButtonError('Only two website buttons are allowed.');
        return;
      }
    }
    if (kind === 'OFFER_CODE') {
      const existing = savedButtons.some((button) => button.type === 'OFFER_CODE');
      if (existing) {
        setButtonError('Offer code button can be added only once.');
        return;
      }
    }
    setButtonDrafts((prev) => [...prev, defaultButtonDraft(kind)]);
    setButtonError('');
  };

  const handleDraftFieldChange = (id, field, value) => {
    setButtonDrafts((prev) =>
      prev.map((draft) =>
        draft.id === id ? { ...draft, fields: { ...draft.fields, [field]: value } } : draft
      )
    );
  };

  const handleDraftSave = (id) => {
    setButtonDrafts((prev) => {
      const target = prev.find((draft) => draft.id === id);
      if (!target) return prev;

      const validation = validateDraft(target);
      if (!validation.ok) {
        setButtonError(validation.message);
        return prev;
      }

      setButtonError('');
      return prev.map((draft) =>
        draft.id === id ? { ...draft, isSaved: true, fields: validation.cleaned } : draft
      );
    });
  };

  const handleDraftRemove = (id) => {
    setButtonDrafts((prev) => prev.filter((draft) => draft.id !== id));
    setButtonError('');
  };

  const handleSubmit = async () => {
    if (!bodyText.trim()) {
      setErrorModal({ open: true, message: 'Body text is required before submitting.' });
      return;
    }

    if (buttonsForPreview.length > MAX_BUTTONS) {
      setErrorModal({ open: true, message: 'Reduce button count to continue.' });
      return;
    }

    try {
      setLoading(true);
      const token = getStoredToken();
      if (!token) throw new Error('Authentication token missing. Please login again.');
      const decoded = jwtDecode(token);
      const payload = buildPayload({
        templateName,
        language,
        headerType,
        headerText,
        headerVariableEnabled,
        headerVariableValue,
        headerMediaHandle,
        bodyText,
        variableFields,
        footerText,
        buttons: buttonsForPreview,
        category: upperCategory,
        userId: decoded?.id,
      });
      console.log('Create template payload JSON:', JSON.stringify(payload, null, 2));
      await createWaTemplate(payload);
      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        navigate('/main/send-template');
      }, 1800);
    } catch (err) {
      console.error('createWaTemplate error', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to create template.';
      setErrorModal({ open: true, message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="template-builder" data-category={upperCategory}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={headerType === 'DOCUMENT' ? '.pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : headerType === 'VIDEO' ? 'video/*' : 'image/*'}
        onChange={handleFileSelection}
      />

      <div className="template-builder__grid">
        <section className="template-builder__panel">
          <section className="template-builder__card">
            <header className="template-builder__card-header">
              <div className="template-meta__icon">
                <FaListUl />
              </div>
              <div>
        <h3>{templateName || 'your_template_name'} • {language}</h3>
        <p>{upperCategory} • {optionDisplay}</p>
              </div>
            </header>
            <div className="template-builder__row">
              <div className="template-builder__field">
                <label htmlFor="template-name">Template name</label>
                <input
                  id="template-name"
                  type="text"
                  maxLength={512}
                  value={templateName}
                  onChange={(event) => {
                    const formatted = event.target.value
                      .replace(/\s+/g, '_')
                      .replace(/[^a-zA-Z0-9_]/g, '')
                      .toLowerCase();
                    setTemplateName(formatted);
                  }}
                  placeholder="Enter template name"
                />
              </div>
              <div className="template-builder__field">
                <label htmlFor="template-language">Language</label>
                <select
                  id="template-language"
                  value={language}
                  onChange={(event) => setLanguage(event.target.value)}
                >
                  <option value="en_US">en_US</option>
                  <option value="es_ES">es_ES</option>
                  <option value="fr_FR">fr_FR</option>
                </select>
              </div>
            </div>
          </section>

          <HeaderConfigurator
            headerType={headerType}
            headerText={headerText}
            onTypeChange={handleHeaderTypeChange}
            onTextChange={setHeaderText}
            headerVariableEnabled={headerVariableEnabled}
            onVariableToggle={handleHeaderVariableToggle}
            headerVariableValue={headerVariableValue}
            onVariableValueChange={setHeaderVariableValue}
            mediaPreview={headerMediaPreview}
            onRetryUpload={() => {
              updateHeaderFileAccept(headerType);
              fileInputRef.current?.click();
            }}
          />

          <BodyEditor
            bodyText={bodyText}
            onBodyTextChange={setBodyText}
            onAddVariable={handleBodyVariableAdd}
            variableFields={variableFields}
            onVariableChange={(index, value) => {
              setVariableFields((prev) =>
                prev.map((item) => (item.index === index ? { ...item, value } : item))
              );
            }}
            onVariableDelete={(index) => {
              setVariableFields((prev) => prev.filter((item) => item.index !== index));
              setBodyText((prev) => removeVariableTokens(prev, index));
            }}
          />

          <FooterEditor footerText={footerText} onFooterTextChange={setFooterText} />

          <ButtonsConfigurator
            drafts={buttonDrafts}
            onAddDraft={handleButtonDraftAdd}
            onFieldChange={handleDraftFieldChange}
            onSaveDraft={handleDraftSave}
            onRemoveDraft={handleDraftRemove}
            error={buttonError}
            category={upperCategory}
          />
        </section>

        <aside className="template-builder__preview">
      <PreviewPanel
        headerType={headerType}
        headerText={headerText}
        mediaPreview={headerMediaPreview}
        bodyText={bodyText}
        footerText={footerText}
        buttons={buttonsForPreview}
      />
        </aside>
      </div>

      <footer className="template-builder__actions">
        <button type="button" className="btn btn-muted" onClick={onBack}>
          Previous
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting…' : 'Submit'}
        </button>
      </footer>

      <UploadStatusModal state={uploadState} onClose={() => setUploadState({ open: false, loading: false, message: '' })} />
      <SimpleModal
        isOpen={errorModal.open}
        onRequestClose={() => setErrorModal({ open: false, message: '' })}
        title="Something went wrong"
        body={errorModal.message}
        confirmLabel="Close"
      />
      <SimpleModal
        isOpen={successModal}
        onRequestClose={() => setSuccessModal(false)}
        title="Template created"
        body="Your template was created successfully. Redirecting to campaign setup…"
        confirmLabel="Close"
      />
    </div>
  );
};

const HeaderConfigurator = ({
  headerType,
  headerText,
  onTypeChange,
  onTextChange,
  headerVariableEnabled,
  onVariableToggle,
  headerVariableValue,
  onVariableValueChange,
  mediaPreview,
  onRetryUpload,
}) => (
  <section className="template-builder__card">
    <header className="template-builder__card-header">
      <div className="template-meta__icon">
        <FaImage />
      </div>
      <div>
        <h3>Header</h3>
        <p className="subtle">Optional • Add media or short text shown above the message body.</p>
      </div>
    </header>

    <div className="template-builder__field">
      <label htmlFor="header-type">Header type</label>
      <select id="header-type" value={headerType} onChange={onTypeChange}>
        {HEADER_TYPES.map((type) => (
          <option key={type} value={type}>
            {type === 'NONE' ? 'None' : type.charAt(0) + type.slice(1).toLowerCase()}
          </option>
        ))}
      </select>
    </div>

    {headerType === 'TEXT' && (
      <div className="template-builder__stack">
        <div className="template-builder__field">
          <label htmlFor="header-text">Header text (max 60 characters)</label>
          <input
            id="header-text"
            type="text"
            maxLength={60}
            value={headerText}
            onChange={(event) => onTextChange(event.target.value)}
            placeholder="Enter header text"
          />
        </div>
        <div className="template-builder__controls">
          <button type="button" className={`btn-pill ${headerVariableEnabled ? 'active' : ''}`} onClick={onVariableToggle}>
            <FaPlus /> Add variable
          </button>
        </div>
        {headerVariableEnabled && (
          <div className="template-builder__field">
            <label>
              Example value for {'{{1}}'}
            </label>
            <input
              type="text"
              value={headerVariableValue}
              onChange={(event) => onVariableValueChange(event.target.value)}
              placeholder="Enter sample value"
            />
          </div>
        )}
      </div>
    )}

    {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
      <div className="template-builder__media">
        {mediaPreview ? (
          <MediaPreview media={mediaPreview} headerType={headerType} onRetry={onRetryUpload} />
        ) : (
          <button type="button" className="btn btn-ghost" onClick={onRetryUpload}>
            <FaCloudUploadAlt /> Upload file
          </button>
        )}
      </div>
    )}

    {headerType === 'LOCATION' && (
      <div className="template-builder__hint">
        <FaMapMarkerAlt /> Location headers send your saved business location with the template.
      </div>
    )}
  </section>
);

const BodyEditor = ({
  bodyText,
  onBodyTextChange,
  onAddVariable,
  variableFields,
  onVariableChange,
  onVariableDelete,
}) => (
  <section className="template-builder__card">
    <header className="template-builder__card-header">
      <div className="template-meta__icon">
        <FaListUl />
      </div>
      <div>
        <h3>Body</h3>
        <p className="subtle">Add the primary content for your message. Use variables for personalised fields.</p>
      </div>
    </header>
    <div className="template-builder__field">
      <label htmlFor="body-text">Body message</label>
      <textarea
        id="body-text"
        value={bodyText}
        onChange={(event) => onBodyTextChange(event.target.value)}
        maxLength={1024}
        placeholder="Type your message…"
        rows={6}
      />
      <div className="template-builder__tools">
        <div className="template-builder__char-count">{bodyText.length} / 1024 characters</div>
        <div className="template-builder__tool-buttons">
          <button type="button" className="icon-button" aria-label="Bold"><FaBold /></button>
          <button type="button" className="icon-button" aria-label="Italic"><FaItalic /></button>
          <button type="button" className="icon-button" aria-label="Emoji"><FaSmile /></button>
          <button type="button" className="icon-button" onClick={onAddVariable}>
            <FaPlus />
            <span>Add variable</span>
          </button>
        </div>
      </div>
    </div>

    {variableFields.length > 0 && (
      <div className="template-builder__variables">
        {variableFields.map(({ index, value }) => (
          <div key={index} className="template-builder__variable">
            <span className="template-builder__variable-token">{`{{${index}}}`}</span>
            <input
              type="text"
              value={value}
              onChange={(event) => onVariableChange(index, event.target.value)}
              placeholder={`Example for {{${index}}}`}
            />
           <button type="button" className="icon-button danger" onClick={() => onVariableDelete(index)}>
             <FaMinusCircle />
           </button>
         </div>
       ))}
     </div>
   )}
  </section>
);

const FooterEditor = ({ footerText, onFooterTextChange }) => (
  <section className="template-builder__card">
    <header className="template-builder__card-header">
      <div className="template-meta__icon">
        <FaFileAlt />
      </div>
      <div>
        <h3>Footer</h3>
        <p className="subtle">Optional sign-off or disclaimer (max 100 characters).</p>
      </div>
    </header>
    <div className="template-builder__field">
      <label htmlFor="footer-text">Footer text</label>
      <input
        id="footer-text"
        type="text"
        value={footerText}
        onChange={(event) => {
          const emojiPattern = /([\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FB00}-\u{1FBFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{1F018}-\u{1F270}]|[\u{1F100}-\u{1F1FF}])/gu;
          if (!emojiPattern.test(event.target.value)) {
            onFooterTextChange(event.target.value);
          }
        }}
        maxLength={100}
        placeholder="Add footer copy"
      />
      <div className="template-builder__char-count">{footerText.length} / 100 characters</div>
    </div>
  </section>
);

const ButtonsConfigurator = ({ drafts, onAddDraft, onFieldChange, onSaveDraft, onRemoveDraft, error, category }) => (
  <section className="template-builder__card">
    <header className="template-builder__card-header">
      <div className="template-meta__icon">
        <FaLink />
      </div>
      <div>
        <h3>Buttons</h3>
        <p className="subtle">
          Let recipients reply quickly or take action. Up to 10 buttons supported.
          {category === 'MARKETING' && ' In marketing template creation one button will be mandatory.'}
        </p>
      </div>
    </header>
    <div className="template-builder__controls">
      <button type="button" className="btn-pill" onClick={() => onAddDraft('QUICK_REPLY')}>
        <FaPlus /> Quick reply
      </button>
      <button type="button" className="btn-pill" onClick={() => onAddDraft('URL')}>
        <FaGlobe /> Website
      </button>
      {category !== 'MARKETING' && category !== 'UTILITY' && (
        <button type="button" className="btn-pill" onClick={() => onAddDraft('OFFER_CODE')}>
          <FaCheckCircle /> COPY_CODE
        </button>
      )}
      <button type="button" className="btn-pill" onClick={() => onAddDraft('PHONE_NUMBER')}>
        <FaPhone /> Phone
      </button>
    </div>
    {error && <div className="template-builder__error">{error}</div>}
    <div className="template-builder__drafts">
      {drafts.map((draft) => (
        <div key={draft.id} className={`button-draft ${draft.isSaved ? 'saved' : ''}`}>
          <div className="button-draft__header">
            <span>{labelForButtonKind(draft.kind)}</span>
            {draft.isSaved && <span className="badge">added</span>}
          </div>
          <ButtonDraftFields draft={draft} onChange={onFieldChange} />
          <div className="button-draft__actions">
            {!draft.isSaved && (
              <button type="button" className="btn btn-primary" onClick={() => onSaveDraft(draft.id)}>
                Add button
              </button>
            )}
            {!(category === 'MARKETING' && drafts.length === 1 && draft.kind === 'QUICK_REPLY') && (
              <button type="button" className="btn btn-muted" onClick={() => onRemoveDraft(draft.id)}>
                <FaTrash /> Remove
              </button>
            )}
          </div>
        </div>
      ))}
      {drafts.length === 0 && <p className="subtle">No buttons added yet.</p>}
    </div>
  </section>
);

const ButtonDraftFields = ({ draft, onChange }) => {
  const disabled = draft.isSaved;
  switch (draft.kind) {
    case 'URL':
      return (
        <div className="button-draft__fields">
          <label>
            URL
            <input
              type="url"
              value={draft.fields.url || ''}
              onChange={(event) => onChange(draft.id, 'url', event.target.value)}
              placeholder="https://example.com"
              disabled={disabled}
            />
          </label>
          <label>
            Button text
            <input
              type="text"
              maxLength={25}
              value={draft.fields.label || ''}
              onChange={(event) => onChange(draft.id, 'label', event.target.value)}
              placeholder="View more"
              disabled={disabled}
            />
          </label>
        </div>
      );
    case 'OFFER_CODE':
      return (
        <div className="button-draft__fields">
          <label>
            Offer code
            <input
              type="text"
              maxLength={20}
              value={draft.fields.offerCode || ''}
              onChange={(event) => onChange(draft.id, 'offerCode', event.target.value)}
              placeholder="SAVE20"
              disabled={disabled}
            />
          </label>
        </div>
      );
    case 'PHONE_NUMBER':
      return (
        <div className="button-draft__fields">
          <label>
            Phone number (10 digits)
            <input
              type="text"
              value={draft.fields.phoneNumber || ''}
              onChange={(event) => {
                const next = event.target.value;
                if (/^\d{0,10}$/.test(next)) {
                  onChange(draft.id, 'phoneNumber', next);
                }
              }}
              placeholder="9876543210"
              disabled={disabled}
            />
          </label>
          <label>
            Button text
            <input
              type="text"
              maxLength={25}
              value={draft.fields.label || ''}
              onChange={(event) => onChange(draft.id, 'label', event.target.value)}
              placeholder="Call us"
              disabled={disabled}
            />
          </label>
        </div>
      );
    default:
      return (
        <div className="button-draft__fields">
          <label>
            Button text
            <input
              type="text"
              maxLength={25}
              value={draft.fields.text || ''}
              onChange={(event) => onChange(draft.id, 'text', event.target.value)}
              placeholder="Yes, interested"
              disabled={disabled}
            />
          </label>
        </div>
      );
  }
};

const MediaPreview = ({ media, headerType, onRetry }) => {
  if (headerType === 'DOCUMENT') {
    return (
      <div className="template-builder__doc-preview">
        <FaFileAlt />
        <div>
          <p>{media.name}</p>
          <button type="button" className="btn btn-ghost" onClick={onRetry}>
            Re-upload
          </button>
        </div>
      </div>
    );
  }

  if (headerType === 'VIDEO') {
    return (
      <div className="template-builder__doc-preview">
        <div className="template-builder__media-added-wrap">
          <div className="template-builder__media-added-row">
            <span>{media.name}</span>
            <span className="badge badge-added">added</span>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onRetry}>
            Re-upload
          </button>
        </div>
      </div>
    );
  }

  if (headerType === 'IMAGE') {
    return (
      <div className="template-builder__doc-preview">
        <div className="template-builder__media-added-wrap">
          <div className="template-builder__media-added-row">
            <span>{media.name}</span>
            <span className="badge badge-added">added</span>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onRetry}>
            Re-upload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="template-builder__media-preview">
      <img src={media.url} alt="Header preview" />
      <button type="button" className="btn btn-ghost" onClick={onRetry}>
        Re-upload
      </button>
    </div>
  );
};

const PreviewPanel = ({ headerType, headerText, mediaPreview, bodyText, footerText, buttons }) => (
  <div className="preview-panel">
    <header className="preview-panel__header">
      <h3>Live preview</h3>
      <p className="subtle">Preview updates as you customise the template.</p>
    </header>
    <div className="preview-panel__bubble">
      {headerType === 'TEXT' && headerText && (
        <div className="preview-panel__header-text">{headerText}</div>
      )}
      {headerType === 'IMAGE' && mediaPreview && (
        <img src={mediaPreview.url} alt="header" className="preview-panel__image" />
      )}
      {headerType === 'VIDEO' && mediaPreview && (
        <video src={mediaPreview.url} className="preview-panel__video" controls />
      )}
      {headerType === 'DOCUMENT' && mediaPreview && (
        <div className="preview-panel__document">
          <FaFileAlt />
          <span>{mediaPreview.name}</span>
        </div>
      )}
      {headerType === 'LOCATION' && (
        <div className="preview-panel__document">
          <FaMapMarkerAlt />
          <span>Shared location</span>
        </div>
      )}
      {bodyText && <div className="preview-panel__body">{bodyText}</div>}
      {footerText && <div className="preview-panel__footer">{footerText}</div>}
    </div>
    {buttons.length > 0 && (
      <div className="preview-panel__buttons">
        {buttons.map((button, index) => (
          <button key={`${button.type}-${index}`} type="button" className="preview-panel__button">
            {button.text}
          </button>
        ))}
      </div>
    )}
  </div>
);

const UploadStatusModal = ({ state, onClose }) => {
  if (!state.open) return null;
  return (
    <div className="upload-modal">
      <div className="upload-modal__content">
        {state.loading ? (
          <>
            <div className="upload-modal__spinner" />
            <p>Uploading file…</p>
          </>
        ) : (
          <>
            <p>{state.message}</p>
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const SimpleModal = ({ isOpen, onRequestClose, title, body, confirmLabel }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onRequestClose}
    className="template-modal"
    overlayClassName="template-modal__overlay"
    contentLabel={title}
  >
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="template-modal__actions">
      <button type="button" className="btn btn-primary" onClick={onRequestClose}>
        {confirmLabel || 'Close'}
      </button>
    </div>
  </Modal>
);

const validateFileForHeader = (file, headerType) => {
  if (headerType === 'IMAGE') {
    return ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
  }
  if (headerType === 'VIDEO') {
    return ['video/mp4', 'video/quicktime', 'video/x-msvideo'].includes(file.type);
  }
  if (headerType === 'DOCUMENT') {
    const allowedDocumentTypes = [
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];
    return allowedDocumentTypes.includes(file.type);
  }
  return true;
};

const buildPayload = ({
  templateName,
  language,
  headerType,
  headerText,
  headerVariableEnabled,
  headerVariableValue,
  headerMediaHandle,
  bodyText,
  variableFields,
  footerText,
  buttons,
  category,
  userId,
}) => {
  const components = [];
  const handleValue = (handle) => handle?.h ?? handle?.data?.h ?? handle?.header_handle ?? null;

  const mediaHandle = handleValue(headerMediaHandle);

  if (headerType === 'TEXT' && headerText) {
    const header = {
      type: 'HEADER',
      format: 'TEXT',
      text: headerText,
    };
    if (headerVariableEnabled) {
      header.example = { header_text: [headerVariableValue || 'Sample value'] };
    }
    components.push(header);
  }

  if (headerType === 'IMAGE' && mediaHandle) {
    components.push({
      type: 'HEADER',
      format: 'IMAGE',
      example: { header_handle: [mediaHandle] },
    });
  }

  if (headerType === 'VIDEO' && mediaHandle) {
    components.push({
      type: 'HEADER',
      format: 'VIDEO',
      example: { header_handle: [mediaHandle] },
    });
  }

  if (headerType === 'DOCUMENT' && mediaHandle) {
    components.push({
      type: 'HEADER',
      format: 'DOCUMENT',
      example: { header_handle: [mediaHandle] },
    });
  }

  if (headerType === 'LOCATION') {
    components.push({ type: 'HEADER', format: 'LOCATION' });
  }

  const bodyComponent = {
    type: 'BODY',
    text: bodyText || 'Your message body {{1}}',
  };

  if (variableFields.length) {
    const sorted = [...variableFields].sort((a, b) => a.index - b.index);
    bodyComponent.example = {
      body_text: [sorted.map((item) => item.value || `Value ${item.index}`)],
    };
  }

  components.push(bodyComponent);

  if (footerText) {
    components.push({ type: 'FOOTER', text: footerText });
  }

  if (buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: buttons.map((button) => {
        if (button.type === 'OFFER_CODE') {
          return {
            type: 'COPY_CODE',
            text: button.text,
          };
        }
        return {
          type: button.type,
          text: button.text,
          ...(button.url ? { url: button.url } : {}),
          ...(button.offerCode ? { offerCode: button.offerCode } : {}),
          ...(button.phone_number ? { phone_number: button.phone_number } : {}),
        };
      }),
    });
  }

  return {
    name: templateName,
    code: {
      name: templateName,
      language,
      category,
      components,
    },
    userid: userId,
  };
};

export default TemplateBuilder;
